import { ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";

import { Section, SectionHeader } from "@/components/marketing/Section";
import { Button } from "@/components/ui/Button";
import { preserveSearch } from "@/lib/nav";

const controls = [
  {
    title: "Review the exact trade",
    text: "An agent-drafted trade proposal pauses for your approval. Check the account, Live or Paper status, security, direction, quantity, and order conditions. Approval applies to that ticket; it does not authorize later trades or a different account.",
    guide: "trading",
    label: "How order approval works",
  },
  {
    title: "Set boundaries per account",
    text: "Optional Risk Controls apply per Portfolio Account. Attended violations require an explicit exception; unattended orders may be resized or paused. Read access does not grant trading access, and an approved order still depends on account permissions and broker handling.",
    guide: "accounts",
    label: "Understand account access",
  },
  {
    title: "Keep connected clients under review",
    text: "Review authorized AI clients in External agents and revoke access when it is no longer needed. For external MCP servers connected to Mavori, choose the tools you enable and pause or remove connections you no longer use.",
    guide: "mcp",
    label: "Manage MCP connections",
  },
];

export function Security() {
  const { search } = useLocation();
  const path = (to: string) => preserveSearch(to, search);

  return (
    <>
      <Section>
        <SectionHeader
          pageTitle
          title="Understand access. Stay in *control*."
          subtitle="Know what you authorize, what happens to your information, and where the limits are before you connect an account or act on an answer."
        />
      </Section>

      <Section className="border-y border-border bg-card/30">
        <SectionHeader title="Controls around your decisions" />
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {controls.map((control) => (
            <article
              key={control.guide}
              className="flex flex-col rounded-[2rem] border border-border bg-card p-7"
            >
              <h3 className="text-xl font-semibold text-foreground">{control.title}</h3>
              <p className="mt-4 flex-1 leading-relaxed text-muted-foreground">{control.text}</p>
              <Button
                variant="link"
                className="mt-5 justify-start px-0"
                to={path(`/docs/${control.guide}`)}
              >
                {control.label}
              </Button>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
          <SectionHeader
            title="Your information and your choices"
            subtitle="The Privacy Policy explains collection, processors, retention, and your rights in full. These are the details to understand first."
          />
          <dl className="space-y-7">
            {[
              [
                "Conversations and attachments",
                "Chat history is stored with your account so you can return to it. Prompts, files you attach, and relevant context are sent to the model provider handling the conversation. Providers operate under their own terms; review the Privacy Policy and choose your model accordingly.",
              ],
              [
                "Optional account connections",
                "Connecting a brokerage is optional. Authorized connections provide account, holdings, and order information for the features you use. Mavori does not receive or store your raw brokerage password, and you can disconnect the account.",
              ],
              [
                "Analytics preferences",
                "You can opt out of product analytics in Settings under Preferences. The Privacy Policy describes usage events, masked session replay, and browser privacy signals.",
              ],
              [
                "Deletion and retention",
                "You can delete conversations or your account. Some records, including order records, may be retained to meet legal, accounting, and audit obligations. Contact us to exercise data access or deletion rights.",
              ],
            ].map(([title, text]) => (
              <div key={title}>
                <dt className="text-lg font-semibold text-foreground">{title}</dt>
                <dd className="mt-2 leading-relaxed text-muted-foreground">{text}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button
            to={path("/legal/privacy")}
            trailing={<ArrowRight className="h-4 w-4" aria-hidden />}
          >
            Read the Privacy Policy
          </Button>
          <Button to={path("/docs/account")} variant="secondary">
            Account settings guide
          </Button>
        </div>
      </Section>

      <Section className="border-t border-border">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">
              A tool for decisions, with limits
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              AI can make mistakes, market data can be incomplete, and a backtest cannot establish
              future performance. An approved order is not a guaranteed fill. Review sources,
              assumptions, and broker status before taking another action.
            </p>
            <Button variant="link" className="mt-4 px-0" to={path("/legal/terms")}>
              Read the Terms of Service
            </Button>
          </div>
          <div className="rounded-[2rem] border border-border bg-card p-7">
            <h2 className="text-2xl font-semibold text-foreground">Report a concern</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Contact us about a suspected vulnerability, unexpected account access, or a privacy
              request. Describe the issue without including passwords, access tokens, or brokerage
              credentials.
            </p>
            <Button variant="secondary" className="mt-5" to={path("/contact")}>
              Contact Mavori
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
